import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  EmailsThemeDto,
  EmailTemplateDto,
  ShopDto,
} from "@babackapp/private-api-client/types";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import useGetAllShopEmailTemplatesSuspense from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { useTemplatesEditorConfig } from "./templates-editor-config-context";
import { useFormContext } from "react-hook-form";
import { EmailTemplateFormData, TemplateType } from "../types";
import { BlockEditorData } from "@babackapp/blocks-editor/types";
import defaultEmailsTheme from "@babackapp/blocks-editor/constants/default-emails-theme";
import { prepareUpsertRequest } from "../lib/prepare-upsert-request";
import useUpsertEmailTemplate from "@babackapp/private-api-client/client/use-upsert-email-template";
import useDeleteEmailTemplate from "@babackapp/private-api-client/client/use-delete-email-template";

interface TemplatesEditorTemplatesContextType {
  templates: EmailTemplateDto[];
  currentTemplate: EmailTemplateDto | null;
  addTemplate: (
    template?: Partial<EmailTemplateDto> & { type?: "email" | "web" },
  ) => Promise<EmailTemplateDto>;
  updateTemplate: (
    updatedTemplate: EmailTemplateFormData,
    id?: string,
  ) => Promise<void>;
  removeTemplate: (templateId: string) => Promise<void>;
}

const TemplatesEditorTemplatesContext = createContext<
  TemplatesEditorTemplatesContextType | undefined
>(undefined);

export interface InitialOverrides {
  customBlocks?: Record<string, unknown>;
  customSubject?: Record<string, string>;
}

export interface TemplatesEditorTemplatesProviderProps {
  children: ReactNode;
  currentTemplateId: string;
  initialOverrides?: InitialOverrides;
}

function getNewTemplateTitle(
  existingTemplates: EmailTemplateDto[],
  baseTitle?: string,
): string {
  let base = baseTitle || "Nouveau template";

  const numberSuffixMatch = base.match(/^(.+?)\s+(\d+)$/);
  if (numberSuffixMatch) {
    base = numberSuffixMatch[1];
  }

  const matchingTitles = existingTemplates
    .map((t) => t.title)
    .filter((title) => title === base || title.startsWith(`${base} `));

  if (matchingTitles.length === 0) {
    return base;
  }

  const numbers = matchingTitles
    .map((title) => {
      if (title === base) return 1;
      const match = title.match(
        new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} (\\d+)$`),
      );
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n): n is number => n !== null);

  const maxNumber = Math.max(...numbers);

  return `${base} ${maxNumber + 1}`;
}

export function TemplatesEditorTemplatesProvider({
  children,
  currentTemplateId,
  initialOverrides,
}: TemplatesEditorTemplatesProviderProps) {
  const { data: shop } = useCurrentShopSuspense();
  const { domain } = useTemplatesEditorConfig();
  const { data: templates = [], refetch: refreshTemplates } =
    useGetAllShopEmailTemplatesSuspense(shop.id, domain);
  const form = useFormContext<EmailTemplateFormData>();
  const upsertTemplateMutation = useUpsertEmailTemplate(shop.id);
  const deleteTemplateMutation = useDeleteEmailTemplate(shop.id);

  // Derive currentTemplate from currentTemplateId prop - no internal state needed!
  const currentTemplate =
    templates?.find((t) => t.id === currentTemplateId) || null;

  const handleUpdate = async (
    data: EmailTemplateFormData,
    id = currentTemplate?.id,
  ): Promise<void> => {
    const { body, hasUploads } = prepareUpsertRequest({
      data: { ...data, domain },
      emailTemplateId: id,
    });
    const response = await upsertTemplateMutation.mutateAsync({
      requestBody: body,
    });

    if (!hasUploads && id === currentTemplate?.id) {
      form.reset(buildFormValues(response.template, shop), {
        keepDirty: false,
      });
    }
    await refreshTemplates();
  };

  const handleDelete = async (templateId: string): Promise<void> => {
    await deleteTemplateMutation.mutateAsync(templateId);
    await refreshTemplates();
  };

  const handleAdd = async (
    template?: Partial<EmailTemplateDto> & { type?: "email" | "web" },
  ): Promise<EmailTemplateDto> => {
    const title = getNewTemplateTitle(templates || [], template?.title);

    const { body } = prepareUpsertRequest({
      data: {
        subject: template?.subject || {},
        preview: template?.preview || {},
        content: (template?.content || {
          beforeBlocks: [],
          afterBlocks: [],
          insideBlocks: [],
        }) as BlockEditorData,
        title,
        assets: { uploads: {} },
        type: template?.type || "email",
        domain,
      },
    });

    const response = await upsertTemplateMutation.mutateAsync({
      requestBody: body,
    });

    await refreshTemplates();
    return response.template as EmailTemplateDto;
  };

  useEffect(() => {
    form.reset(buildFormValues(currentTemplate, shop, initialOverrides), {
      keepDirty: false,
    });
  }, [currentTemplate, initialOverrides]);

  const value: TemplatesEditorTemplatesContextType = useMemo(
    () => ({
      templates: templates || [],
      currentTemplate,
      updateTemplate: handleUpdate,
      addTemplate: handleAdd,
      removeTemplate: handleDelete,
    }),
    [templates, currentTemplate],
  );

  return (
    <TemplatesEditorTemplatesContext.Provider value={value}>
      {children}
    </TemplatesEditorTemplatesContext.Provider>
  );
}

export function useTemplatesEditorTemplates() {
  const context = useContext(TemplatesEditorTemplatesContext);

  if (context === undefined) {
    throw new Error(
      "useTemplatesEditorTemplates must be used within a TemplatesEditorTemplatesProvider",
    );
  }
  return context;
}

function buildFormValues(
  template: EmailTemplateDto | null,
  shop: ShopDto,
  overrides?: InitialOverrides,
): EmailTemplateFormData {
  let content = (template?.content || {
    beforeBlocks: [],
    afterBlocks: [],
    insideBlocks: [],
  }) as BlockEditorData;

  let subject = template?.subject || {};

  if (overrides?.customBlocks) {
    content = {
      ...content,
      beforeBlocks: applyCustomBlocksToArray(
        content.beforeBlocks,
        overrides.customBlocks,
      ),
      insideBlocks: applyCustomBlocksToArray(
        content.insideBlocks,
        overrides.customBlocks,
      ),
      afterBlocks: applyCustomBlocksToArray(
        content.afterBlocks,
        overrides.customBlocks,
      ),
    };
  }

  if (overrides?.customSubject) {
    subject = { ...subject, ...overrides.customSubject };
  }

  return {
    subject,
    preview: template?.preview || {},
    content,
    assets: { uploads: {} },
    title: template?.title || "Sans nom",
    theme:
      (shop.trackingPreferences?.emailsTheme as NonNullable<
        Required<EmailsThemeDto>
      >) || defaultEmailsTheme,
    type: (template?.type || "email") as TemplateType,
  };
}

function applyCustomBlocksToArray(
  blocks: BlockEditorData["beforeBlocks"],
  customBlocks: Record<string, unknown>,
): BlockEditorData["beforeBlocks"] {
  return blocks.map((block) => {
    if (block.id in customBlocks) {
      return customBlocks[block.id] as typeof block;
    }
    return block;
  });
}
